import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Dialog, DialogContent } from 'components/ui/dialog'
import { Button } from 'components/ui/button'
import { Skeleton } from 'components/ui/skeleton'
import { File, X, Trash2, FileText, Image, Code, FileSpreadsheet, FilePlus, Loader2 } from 'lucide-react'
import { useAddFilesToProject, useDeleteFileFromProject, useFetchProjectFiles } from '../hooks/use-projects';
import { ProjectFileResponse } from '../services/projects-services';
import { getFile } from '../../../services/storage-service';
import { useConfig } from '../../../hooks/use-config';
import { useToast } from '../../../hooks/use-toast';
import { useTranslation } from 'react-i18next'

// import { useTranslation } from 'react-i18next'

interface FileItem {
    id: string;
    file: File;
    name: string;
    size: string;
    type: string;
}

const allowedExtensions = ['.pdf', '.doc', '.docx', '.txt', '.md', '.xml', '.csv', '.xlsx', '.xls', '.png', '.jpg', '.jpeg', '.heic'];

interface ExtendedProjectFileResponse extends ProjectFileResponse {
    loading?: boolean;
    fileUrl?: string;
}

function AddFilesModal({ projectName, isOpen, setIsOpen, projectId }: { projectName: string, isOpen: boolean, setIsOpen: (isOpen: boolean) => void, projectId: string }) {
    const { t } = useTranslation();
    const [page, setPage] = useState(1);
    const loaderRef = useRef<HTMLDivElement | null>(null);
    const fetchingFileIdsRef = useRef<Set<string>>(new Set());
    const { configurationSettings } = useConfig();

    const [isDragOver, setIsDragOver] = useState(false);
    // console.log('isDragOver', isDragOver)
    const [selectedFiles, setSelectedFiles] = useState<ExtendedProjectFileResponse[]>([]);
    const [draggedFiles, setDraggedFiles] = useState<File[]>([]);
    // const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(new Set());
    const [deletingFileId, setDeletingFileId] = useState<string | null>(null);

    // const { t } = useTranslation();
    const { data: projectFiles, isFetching: isFetchingProjectFiles } = useFetchProjectFiles({ projectId: projectId, page: page, pageSize: 10 });
    const totalFilesCount = projectFiles?.totalCount ?? 0;

    const allLoaded = totalFilesCount > 0 && selectedFiles.length >= totalFilesCount;

    console.log('allLoaded', allLoaded, "projectFiles", projectFiles, "selectedFiles", selectedFiles, "totalFilesCount", totalFilesCount)


    useEffect(() => {
        if (!isOpen || allLoaded) return;

        const currentLoader = loaderRef.current;
        if (!currentLoader) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    // Use functional update to get latest state values
                    setPage((prevPage) => {
                        // Recalculate allLoaded with current values
                        const currentTotalCount = projectFiles?.totalCount ?? 0;
                        const currentSelectedCount = selectedFiles.length;
                        const isAllLoaded = currentTotalCount > 0 && currentSelectedCount >= currentTotalCount;

                        if (!isFetchingProjectFiles && !isAllLoaded) {
                            console.log('Loading next page. Current page:', prevPage, 'Selected files:', currentSelectedCount, 'Total:', currentTotalCount);
                            return prevPage + 1;
                        }
                        return prevPage;
                    });
                }
            },
            { threshold: 0.1, rootMargin: '100px' }
        );

        observer.observe(currentLoader);

        return () => {
            observer.unobserve(currentLoader);
        };
    }, [isOpen, allLoaded, isFetchingProjectFiles, selectedFiles.length, projectFiles?.totalCount]);


    // console.log('draggedFiles', draggedFiles)
    const targetAreaRef = useRef<HTMLDivElement>(null);
    const { mutateAsync: addFilesToProject, isPending: isAddingFiles } = useAddFilesToProject(projectId);
    console.log('isAddingFiles', isAddingFiles)
    const { mutateAsync: deleteFileFromProject } = useDeleteFileFromProject(projectId);
    const { toast } = useToast();

    const fetchFileUrl = useCallback(async (fileId: string): Promise<string | undefined> => {
        // Prevent duplicate API calls
        if (fetchingFileIdsRef.current.has(fileId)) {
            return undefined;
        }

        fetchingFileIdsRef.current.add(fileId);
        try {
            const fileResponse = await getFile(fileId, configurationSettings);
            fetchingFileIdsRef.current.delete(fileId);
            return fileResponse.url;
        } catch (error) {
            console.error('Error fetching file URL:', error);
            fetchingFileIdsRef.current.delete(fileId);
            return undefined;
        }
    }, [configurationSettings]);

    const handleAddFiles = async (files: FileItem[]) => {
        setDraggedFiles([]);
        const uniqueErrors = new Set<string>();

        for (const file of files) {
            try {
                const fileResponse = await addFilesToProject(file.file);
                // console.log('fileResponse in handleAddFiles', fileResponse)
                const extendedFile: ExtendedProjectFileResponse = {
                    ...fileResponse,
                    loading: true,
                    fileUrl: undefined,
                };
                setSelectedFiles(prev => [...prev, extendedFile]);
                try {
                    const fileUrl = await fetchFileUrl(fileResponse.fileId);
                    setSelectedFiles(prev => prev.map(f =>
                        f.fileId === fileResponse.fileId
                            ? { ...f, loading: false, fileUrl }
                            : f
                    ));
                } catch (urlError) {
                    console.error('Error fetching file URL:', urlError);
                    // Update file to remove loading state even if URL fetch fails
                    setSelectedFiles(prev => prev.map(f =>
                        f.fileId === fileResponse.fileId
                            ? { ...f, loading: false }
                            : f
                    ));
                }
            } catch (error: any) {
                console.error('Error uploading file:', error);

                let errorMessage = `Failed to upload file: ${file.name}`;

                if (error?.message) {
                    errorMessage = error.message;
                } else if (error?.error) {
                    if (typeof error.error === 'string') {
                        errorMessage = error.error;
                    } else if (error.error?.message) {
                        errorMessage = error.error.message;
                    } else if (error.error?.detail) {
                        errorMessage = error.error.detail;
                    }
                } else if (error?.detail) {
                    errorMessage = error.detail;
                }

                uniqueErrors.add(errorMessage);
            }
        }

        // Show all unique errors after processing all files
        if (uniqueErrors.size > 0) {
            const errorMessages = Array.from(uniqueErrors);
            const errorDescription = errorMessages.length === 1
                ? errorMessages[0]
                : errorMessages.join('\n');

            toast({
                title: 'Upload Failed',
                description: errorDescription,
                variant: 'destructive',
            });
        }
    }

    // Reset page and selectedFiles when modal opens or projectId changes
    useEffect(() => {
        if (isOpen) {
            setPage(1);
            setSelectedFiles([]);

            fetchingFileIdsRef.current.clear();
        }
    }, [isOpen, projectId]);

    useEffect(() => {
        if (projectFiles && projectFiles.data.length > 0 && !isFetchingProjectFiles && isOpen) {
            setSelectedFiles((prev) => {
                const existingFileIds = new Set(prev.map(file => file.fileId));
                const newFiles = projectFiles.data.filter(file => !existingFileIds.has(file.fileId));
                const extendedFiles: ExtendedProjectFileResponse[] = newFiles.map(file => ({
                    ...file,
                    loading: true,
                    fileUrl: undefined,
                }));

                extendedFiles.forEach(async (file) => {
                    const fileUrl = await fetchFileUrl(file.fileId);
                    setSelectedFiles(current => current.map(f =>
                        f.fileId === file.fileId && !f.fileUrl
                            ? { ...f, loading: false, fileUrl }
                            : f
                    ));
                });

                return [...prev, ...extendedFiles];
            });
        }
    }, [projectFiles, isFetchingProjectFiles, isOpen, fetchFileUrl]);


    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';


        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const getFileIcon = (fileItem: ExtendedProjectFileResponse, file?: File) => {
        const extension = fileItem.extension || '';
        // Normalize extension (remove leading dot if present, make lowercase)
        const normalizedExt = extension.replace(/^\./, '').toLowerCase();
        const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'svg', 'bmp', 'ico'];
        const isImage = imageExtensions.includes(normalizedExt) || extension.startsWith('image/');

        if (isImage) {
            // Show loading spinner while fetching file URL
            if (fileItem.loading) {
                return (
                    <div className="w-8 h-8 rounded border overflow-hidden flex items-center justify-center">
                        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                    </div>
                );
            }

            // Show actual image if fileUrl is available
            if (fileItem.fileUrl) {
                return (
                    <div className="w-8 h-8 rounded border overflow-hidden">
                        <img
                            src={fileItem.fileUrl}
                            alt={fileItem.fileName}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                                // Fallback to icon if image fails to load
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                            }}
                        />
                    </div>
                );
            }

            // Show image preview if file is provided (for newly uploaded files)
            if (file) {
                return (
                    <div className="w-8 h-8 rounded border overflow-hidden">
                        <img
                            src={URL.createObjectURL(file)}
                            alt="Preview"
                            className="w-full h-full object-cover"
                            onLoad={(e) => {
                                // Clean up the object URL after the image loads to prevent memory leaks
                                setTimeout(() => URL.revokeObjectURL((e.target as HTMLImageElement).src), 1000);
                            }}
                        />
                    </div>
                );
            }

            return <Image className="w-4 h-4" />;
        }

        if (extension.includes('text/') || extension.includes('application/pdf') || extension.includes('document') || normalizedExt.match(/^(pdf|doc|docx|txt|md)$/)) return <FileText className="w-4 h-4" />;
        if (extension.includes('code') || extension.includes('javascript') || extension.includes('json') || extension.includes('html') || extension.includes('css') || normalizedExt.match(/^(js|ts|jsx|tsx|json|html|css)$/)) return <Code className="w-4 h-4" />;
        if (extension.includes('spreadsheet') || extension.includes('excel') || extension.includes('csv') || normalizedExt.match(/^(xlsx|xls|csv)$/)) return <FileSpreadsheet className="w-4 h-4" />;
        return <File className="w-4 h-4" />;
    };

    const handleFiles = (files: FileList | null) => {
        if (files && files.length > 0) {
            const newFiles: FileItem[] = Array.from(files).map(file => ({
                id: Math.random().toString(36).substr(2, 9),
                file,
                name: file.name,
                size: formatFileSize(file.size),
                type: file.type || 'unknown'
            }));
            handleAddFiles(newFiles);
        }
    };

    const removeFile = async (id: string) => {
        // If already deleting, don't allow another deletion
        if (deletingFileId !== null) {
            return;
        }

        try {
            setDeletingFileId(id);
            await deleteFileFromProject([id]);
            setSelectedFiles(prev => prev?.filter(file => file.fileId !== id) ?? []);
        } catch (error) {
            console.error('Error deleting file:', error);
        } finally {
            setDeletingFileId(null);
        }
    };

    const addinputFiles = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.multiple = true;
        input.accept = allowedExtensions.join(',');
        input.onchange = (e) => {
            handleFiles((e.target as HTMLInputElement).files);
        };
        input.click();
    }

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()

        if (!isDragOver) setIsDragOver(true)

        const files = Array.from(e.dataTransfer.items)
            .map(item => item.getAsFile())
            .filter(Boolean) as File[]
        setDraggedFiles(files)
    }

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();

        // Check if we're leaving the modal entirely
        const targetArea = targetAreaRef.current;
        const rect = targetArea?.getBoundingClientRect();

        if (rect) {
            const { clientX, clientY } = e;
            const isOutsideModal =
                clientX < rect.left ||
                clientX > rect.right ||
                clientY < rect.top ||
                clientY > rect.bottom;

            if (isOutsideModal) {

                setIsDragOver(false);
                setDraggedFiles([]);
            }
        }
    };


    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        // console.log('handleDrop', e.dataTransfer.files
        setIsDragOver(false)
        setDraggedFiles([])

        const files = e.dataTransfer.files
        handleFiles(files)
    }



    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>

            <DialogContent className=" max-w-4xl h-[60vh] w-[calc(100%-2rem)]  sm:w-[calc(100%-3rem)] sm:max-w-[800px] p-4 flex flex-col" showOverlay={true} hideClose={true}>
                <div className='relative h-full flex flex-col gap-4 ' ref={targetAreaRef}>

                    <div className='flex items-center justify-between'>
                        <div className="space-y-2 pb-3 flex-shrink-0">
                            <h2 className="text-[16px] sm:text-[18px] font-light text-foreground leading-[24px] sm:leading-[28px]">
                                {t('PROJECT_FILES')}
                            </h2>
                        </div>
                        <div className='flex items-center gap-2'>
                            <Button variant={"outline"} id="add-files-button" className="p-2 rounded-xl border" onClick={() => {
                                addinputFiles();
                            }}>

                                <span className="text-sm font-normal">{t('ADD_FILES')}</span>
                            </Button>
                            <X className="w-5 h-5 cursor-pointer" onClick={() => {
                                setIsOpen(false);
                                setPage(1);
                                setSelectedFiles([]);
                                setDraggedFiles([]);
                                setDeletingFileId(null);
                            }} />

                        </div>

                    </div>


                    {/* File upload area */}
                    {
                        selectedFiles.length === 0 && !isAddingFiles &&
                        <div className="h-full"
                            onDragEnter={handleDragEnter}
                            onDragOver={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                        >
                            <div
                                className={`flex flex-col h-full justify-center items-center border rounded-xl p-4 md:p-8 transition-all duration-200 cursor-pointer group border-muted-foreground/25 bg-muted/5 hover:bg-muted/10 hover:border-muted-foreground/40'
                                    }`}
                                onClick={addinputFiles}

                            >
                                <div className="flex flex-col items-center text-sm max-w-full md:max-w-sm" >
                                    <div className="p-4 rounded-full  transition-colors duration-200">
                                        <FilePlus className="w-6 h-6 transition-colors duration-200 font-normal" />
                                    </div>

                                    <div className="text-left sm:text-center"><div className=" text-xs sm:text-sm font-light">{t('ADD_DOCUMENTS_CODE_FILES_IMAGES_AND_MORE')} <span className="font-bold">{projectName}</span> {t('CAN_ACCESS_CONTENTS_WHEN_CHAT_INSIDE_PROJECT')}</div></div>

                                </div>


                            </div>


                        </div>
                    }

                    {/* File List */}
                    {selectedFiles.length > 0 && (
                        <>
                            <div className="space-y-2 h-[calc(100%-100px)] overflow-y-auto"
                                onDragEnter={handleDragEnter}
                                onDragOver={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                            >


                                {selectedFiles?.map((fileItem) => (
                                    <div key={fileItem.fileId} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border">
                                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                                            <div className="flex-shrink-0 text-muted-foreground">
                                                {getFileIcon(fileItem)}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-foreground truncate">
                                                    {fileItem.fileName}
                                                </p>
                                                <p className="text-xs text-muted-foreground">
                                                    {formatFileSize(fileItem.size)} • {fileItem.extension || 'Unknown type'}
                                                </p>
                                            </div>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => removeFile(fileItem.fileId)}
                                            className="flex-shrink-0 h-8 w-8 p-0 hover:bg-destructive/10 hover:text-destructive"
                                            disabled={deletingFileId !== null}
                                        >
                                            {deletingFileId === fileItem.fileId ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <Trash2 className="w-4 h-4" />
                                            )}
                                        </Button>
                                    </div>
                                ))}
                                {isAddingFiles && (
                                    <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border">
                                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                                            <div className="flex-shrink-0 text-muted-foreground">
                                                <Skeleton className="w-4 h-4 rounded" />
                                            </div>
                                            <div className="flex-1 min-w-0 space-y-2">
                                                <Skeleton className="h-4 w-32" />
                                                <Skeleton className="h-3 w-24" />
                                            </div>
                                        </div>
                                        <Skeleton className="h-8 w-8 rounded" />
                                    </div>
                                )}

                            </div>



                            {!allLoaded && <div ref={loaderRef} className="p-4 text-center text-sm text-neutral-400">
                            </div>}

                        </>
                    )}

                    {isAddingFiles && selectedFiles.length === 0 && (
                        <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border">
                            <div className="flex items-center space-x-3 flex-1 min-w-0">
                                <div className="flex-shrink-0 text-muted-foreground">
                                    <Skeleton className="w-4 h-4 rounded" />
                                </div>
                                <div className="flex-1 min-w-0 space-y-2">
                                    <Skeleton className="h-4 w-32" />
                                    <Skeleton className="h-3 w-24" />
                                </div>
                            </div>
                            <Skeleton className="h-8 w-8 rounded" />
                        </div>
                    )}


                    {isDragOver && (
                        <div
                            className="absolute inset-0 bg-[#ffffffb5] rounded-xl flex flex-col items-center justify-center z-10"
                            onDragOver={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                            onDrop={handleDrop}
                        >
                            <div className="bg-background/95 backdrop-blur-sm rounded-lg p-6 shadow-lg  max-w-md w-full mx-4">
                                <div className="text-center space-y-4">
                                    <div className="p-3 rounded-full bg-primary/20 mx-auto w-fit">
                                        <FilePlus className="w-6 h-6 text-primary" />
                                    </div>

                                    <div>
                                        <h4 className="text-lg font-semibold text-foreground mb-2">
                                            {draggedFiles.length > 0
                                                ? `${draggedFiles.length} ${t('FILE')} ${draggedFiles.length > 1 ? t('FILES') : ''} ${t('READY_TO_ADD')}`
                                                : t('DROP_ANY_FILE_HERE_TO_ADD_IN_THE_PROJECT')
                                            }
                                        </h4>

                                        {draggedFiles.length > 0 && (
                                            <div className="space-y-2 max-h-32 overflow-y-auto">
                                                {draggedFiles.slice(0, 5).map((file, index) => (
                                                    <div key={index} className="flex items-center space-x-2 text-sm">
                                                        <div className="flex-shrink-0">
                                                            {file.type?.startsWith('image/') && file ? (
                                                                <img
                                                                    src={URL.createObjectURL(file)}
                                                                    alt={file.name}
                                                                    className="w-4 h-4 object-cover rounded"
                                                                    onLoad={(e) => {
                                                                        setTimeout(() => URL.revokeObjectURL((e.target as HTMLImageElement).src), 1000);
                                                                    }}
                                                                />
                                                            ) : (
                                                                <File className="w-4 h-4" />
                                                            )}
                                                        </div>
                                                        <span className="text-foreground truncate flex-1">{file.name}</span>
                                                        <span className="text-muted-foreground text-xs">
                                                            {formatFileSize(file.size)}
                                                        </span>
                                                    </div>
                                                ))}
                                                {draggedFiles.length > 5 && (
                                                    <p className="text-xs text-muted-foreground text-center">
                                                        ... and {draggedFiles.length - 5} more files
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <p className="text-sm text-muted-foreground">
                                        {t('RELEASE_TO_ADD_THESE_FILES_TO_YOUR_PROJECT')}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}




                </div>
                <div id="file-input" className="hidden">
                    <input type="file" multiple accept={allowedExtensions.join(',')} />
                </div>


            </DialogContent>

        </Dialog >
    )
}

export default AddFilesModal